import { DateTimeValueSchemas } from "@validereinc/utilities";
import { z } from "zod";
import {
  createCreateOneRequestSchema,
  createGetListRequestSchema,
  createGetOneRequestSchema,
  createUpdateOneRequestSchema,
  DomainModelMetaExtendedSchema,
  DomainModelMetaSchema,
  DomainModelReferenceSchema,
  DomainModelSchema,
  IdentifierSchema,
  type TypeOfObjectValues,
} from "../util/index.ts";
import { CustomAttributeRecordSchema } from "./CustomAttributeSchemas.ts";
import { EquipmentSchema } from "./EquipmentSchemas.ts";
import { FacilitySchema } from "./FacilitySchemas.ts";

export const FlowStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;
export type FlowStatusType = (typeof FlowStatus)[keyof typeof FlowStatus];

export const FlowTypes = {
  fuel: "fuel",
  flare: "flare",
  vent: "vent",
  fugitive: "fugitive",
  productionVolume: "production_volume",
  supplyChain: "supply_chain",
  receipt: "receipt",
  disposition: "disposition",
} as const;
export type FlowTypesType = (typeof FlowTypes)[keyof typeof FlowTypes];

export const FlowProductTypes = {
  naturalGas: "natural_gas",
  electricity: "electricity",
  steam: "steam",
  crudeOil: "crude_oil",
} as const;
export type FlowProductTypesType = TypeOfObjectValues<typeof FlowProductTypes>;

export const FlowAttributeReferenceSchema = DomainModelReferenceSchema;
export type FlowAttributeReferenceType = z.infer<
  typeof FlowAttributeReferenceSchema
>;

export const FlowEquipmentSchema = EquipmentSchema.pick({
  name: true,
  status: true,
  type_id: true,
  effective_date: true,
  custom_attributes: true,
  latitude: true,
  longitude: true,
}).merge(
  z.object({
    method: z.string(),
    measurement_subject_id: z.string(),
  })
);
export type FlowEquipmentType = z.infer<typeof FlowEquipmentSchema>;

export const FlowFacilitySchema = FacilitySchema.pick({
  name: true,
  status: true,
  company_id: true,
  custom_attributes: true,
}).merge(
  z.object({
    measurement_subject_id: z.string(),
  })
);
export type FlowFacilityType = z.infer<typeof FlowFacilitySchema>;

const FlowCoreSchema = z
  .object({
    name: z.string(),
    type: z.nativeEnum(FlowTypes),
    status: z.nativeEnum(FlowStatus),
    product_category: z.nativeEnum(FlowProductTypes),
    product_type: z.nativeEnum(FlowProductTypes),
    custom_attributes: CustomAttributeRecordSchema,
    associated_facility_id: IdentifierSchema.optional(),
    associated_facility: FlowFacilitySchema.optional(),
    associated_equipment_id: IdentifierSchema.optional(),
    associated_equipment: FlowEquipmentSchema.optional(),
    upstream_facility_id: IdentifierSchema.optional(),
    upstream_facility: FlowFacilitySchema.optional(),
    downstream_facility_id: IdentifierSchema.optional(),
    downstream_facility: FlowFacilitySchema.optional(),
    upstream_equipment_id: IdentifierSchema.optional(),
    upstream_equipment: FlowEquipmentSchema.optional(),
    downstream_equipment_id: IdentifierSchema.optional(),
    downstream_equipment: FlowEquipmentSchema.optional(),
    upstream_flow_ids: z.array(IdentifierSchema).optional(),
    downstream_flow_ids: z.array(IdentifierSchema).optional(),
  })
  .merge(DomainModelSchema)
  .merge(DomainModelMetaSchema)
  .merge(DomainModelMetaExtendedSchema);
type FlowCoreType = z.infer<typeof FlowCoreSchema> & {
  upstream_flows?: FlowCoreType[];
  downstream_flows?: FlowCoreType[];
};

export const FlowSchema = FlowCoreSchema.extend({
  upstream_flows: z.lazy(() => z.array(FlowCoreSchema)).optional(),
  downstream_flows: z.lazy(() => z.array(FlowCoreSchema)).optional(),
}) satisfies z.ZodType<FlowCoreType>;
export type FlowType = z.infer<typeof FlowSchema>;

export const FlowV2Schema = FlowSchema.extend({
  upstream_flows_count: z.number().min(0).optional(),
  downstream_flows_count: z.number().min(0).optional(),
});
export type FlowV2Type = z.infer<typeof FlowV2Schema>;

export const FlowGetListRequestSchema = createGetListRequestSchema(
  z
    .object({
      id: z.string().uuid().or(z.array(z.string().uuid())),
      type: z.nativeEnum(FlowTypes),
      product_type: z.nativeEnum(FlowProductTypes),
      product_category: z.nativeEnum(FlowProductTypes), // REVIEW: is this right?
      "downstream_flows.id": z.string().uuid(),
      "upstream_flows.id": z.string().uuid(),
    })
    .partial(),
  z
    .object({
      period: DateTimeValueSchemas.dateYearMonth,
    })
    .partial()
);
export type FlowGetListRequestType = z.infer<typeof FlowGetListRequestSchema>;

export const FlowGetOneRequestSchema = createGetOneRequestSchema(
  z
    .object({
      period: DateTimeValueSchemas.dateYearMonth,
    })
    .partial()
);
export type FlowGetOneRequestType = z.infer<typeof FlowGetOneRequestSchema>;

export const FlowCreateOneRequestSchema = createCreateOneRequestSchema(
  FlowSchema.pick({
    name: true,
    type: true,
    status: true,
    product_category: true,
    product_type: true,
    custom_attributes: true,
    upstream_flow_ids: true,
    upstream_facility_id: true,
    upstream_equipment_id: true,
    downstream_flow_ids: true,
    downstream_facility_id: true,
    downstream_equipment_id: true,
  })
);
export type FlowCreateOneRequestType = z.infer<
  typeof FlowCreateOneRequestSchema
>;

export const FlowUpdateOneRequestSchema = createUpdateOneRequestSchema(
  FlowSchema.pick({
    name: true,
    type: true,
    status: true,
    product_category: true,
    product_type: true,
    custom_attributes: true,
    upstream_flow_ids: true,
    upstream_facility_id: true,
    upstream_equipment_id: true,
    downstream_flow_ids: true,
    downstream_facility_id: true,
    downstream_equipment_id: true,
  }).partial()
);
export type FlowUpdateOneRequestType = z.infer<
  typeof FlowUpdateOneRequestSchema
>;

export const FlowAttributeGetListRequestSchema = createGetListRequestSchema(
  z.object({}),
  z.never()
);
export type FlowAttributeGetListRequestType = z.infer<
  typeof FlowAttributeGetListRequestSchema
>;
